// A Pénzügy ÖSSZEGZÉS saját grafikonjai — a közös chart-készlet (`components/shared/charts`) nyelvén.
//
//  • `CashflowChart` — havonta a bevétel és a kiadás egymás mellett, rajta az egyenleg vonala. A bevétel-oszlop
//    a LEZÁRT hónapon a kiszámlázott TÉNY, a nyitott (és az adat-horizont előtti) hónapon a TERV — halványabb
//    oszloppal, hogy a becsült szám ne olvasódjon ugyanolyan biztosnak.
//  • `StackedTrendChart` — sorozatonként (kiadás-főkategóriák) halmozott havi oszlop.
//
// Mindkettő `aria-hidden` vizuál + `sr-only` adat-lista (a közös `SrOnlyData`), mint a többi chart.

import { subtractDecimals } from '@atlas/shared';
import { useTranslation } from 'react-i18next';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ComposedChart,
  Rectangle,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
  type BarShapeProps,
} from 'recharts';

import {
  AXIS_LINE,
  MoneyTick,
  SrOnlyData,
  TICK_STYLE,
  TooltipCard,
  toNum,
  type Formatter,
} from '@/components/shared/charts';
import { formatPercent } from '@/lib/format-percent';

/** Egy jelmagyarázat-tétel: szín-pötty + címke. */
function LegendItem({ swatch, label }: { swatch: string; label: string }): JSX.Element {
  return (
    <span className="text-muted-foreground flex items-center gap-1.5">
      <span className={`${swatch} size-2 shrink-0 rounded-full`} aria-hidden="true" />
      {label}
    </span>
  );
}

export interface CashflowRow {
  month: string;
  /** A hónap bevétele: lezárt hónapon a tény, egyébként a terv (`revenueEstimated`). */
  revenue: string;
  revenueEstimated: boolean;
  /** A hónap forint nettó kiadása. */
  expense: string;
}

export function CashflowChart({
  rows: input,
  fmt,
  fmtShort,
  monthLabel,
  labels,
  ariaLabel,
}: {
  rows: CashflowRow[];
  fmt: Formatter;
  fmtShort: Formatter;
  monthLabel: (month: string) => string;
  labels: {
    revenue: string;
    revenuePlan: string;
    expense: string;
    balance: string;
    margin: string;
  };
  ariaLabel: string;
}): JSX.Element {
  const rows = input.map((r) => {
    const balance = subtractDecimals(r.revenue, r.expense);
    const revenueValue = toNum(r.revenue);
    return {
      ...r,
      label: monthLabel(r.month),
      balance,
      revenueValue,
      expenseValue: toNum(r.expense),
      balanceValue: toNum(balance),
      // a HASZONKULCS csak megjelenítés (arány), pénz-összeget nem ebből számolunk
      marginPct: revenueValue > 0 ? Math.round((toNum(balance) / revenueValue) * 100) : null,
    };
  });
  const hasEstimate = rows.some((r) => r.revenueEstimated);
  const { i18n } = useTranslation();

  return (
    <div>
      <div className="mb-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs">
        <LegendItem swatch="bg-success" label={labels.revenue} />
        {hasEstimate && <LegendItem swatch="bg-success/40" label={labels.revenuePlan} />}
        <LegendItem swatch="bg-danger-500/70" label={labels.expense} />
        <LegendItem swatch="bg-violet" label={labels.balance} />
      </div>
      <div className="text-muted-foreground h-64 w-full" aria-hidden="true">
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart
            data={rows}
            margin={{ top: 8, right: 4, bottom: 0, left: 4 }}
            barGap={2}
            barCategoryGap="20%"
            accessibilityLayer
          >
            <CartesianGrid vertical={false} stroke={AXIS_LINE} strokeDasharray="3 3" />
            <XAxis
              dataKey="label"
              tick={TICK_STYLE}
              tickLine={false}
              axisLine={{ stroke: AXIS_LINE }}
              interval="preserveStartEnd"
              minTickGap={16}
            />
            <YAxis
              tick={<MoneyTick format={fmtShort} />}
              tickLine={false}
              axisLine={false}
              width={72}
            />
            <Tooltip
              cursor={{ className: 'fill-muted/40' }}
              content={({ active, payload }) => {
                const row = payload?.[0]?.payload as (typeof rows)[number] | undefined;
                if (!active || !row) return null;
                return (
                  <TooltipCard
                    label={row.label}
                    rows={[
                      {
                        key: 'rev',
                        label: row.revenueEstimated ? labels.revenuePlan : labels.revenue,
                        value: fmt(row.revenue),
                        swatch: row.revenueEstimated ? 'bg-success/40' : 'bg-success',
                      },
                      {
                        key: 'exp',
                        label: labels.expense,
                        value: fmt(row.expense),
                        swatch: 'bg-danger-500/70',
                      },
                      {
                        key: 'bal',
                        label: labels.balance,
                        value: fmt(row.balance),
                        swatch: 'bg-violet',
                      },
                      ...(row.marginPct === null
                        ? []
                        : [
                            {
                              key: 'margin',
                              label: labels.margin,
                              value: formatPercent(row.marginPct, i18n.language, {
                                fractionDigits: 0,
                              }),
                            },
                          ]),
                    ]}
                  />
                );
              }}
            />
            <Bar dataKey="revenueValue" radius={[3, 3, 0, 0]} maxBarSize={18}>
              {rows.map((r) => (
                <Cell
                  key={r.month}
                  className={r.revenueEstimated ? 'fill-success/40' : 'fill-success'}
                />
              ))}
            </Bar>
            <Bar
              dataKey="expenseValue"
              className="fill-danger-500/70"
              radius={[3, 3, 0, 0]}
              maxBarSize={18}
            />
            {/* Az EGYENLEG lila: a márkaszín ebben a témában a bevétel zöldje, a két jel összefolyna. A `className`
                a vonal CSOPORTJÁRA kerül, a görbe saját `stroke` attribútumot kap (Recharts-alap: kék) — ezért a
                görbét célzottan színezzük. */}
            <Line
              type="monotone"
              dataKey="balanceValue"
              className="[&_.recharts-line-curve]:stroke-violet"
              strokeWidth={2}
              dot={{ r: 2.5, className: 'fill-violet stroke-background', strokeWidth: 1 }}
              activeDot={{ r: 4, className: 'fill-violet stroke-background', strokeWidth: 2 }}
            />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
      <SrOnlyData
        ariaLabel={ariaLabel}
        items={rows.map((r) => ({
          key: r.month,
          label: r.label,
          value:
            `${r.revenueEstimated ? labels.revenuePlan : labels.revenue} ${fmt(r.revenue)}, ` +
            `${labels.expense} ${fmt(r.expense)}, ${labels.balance} ${fmt(r.balance)}`,
        }))}
      />
    </div>
  );
}

export interface TrendSeries {
  key: string;
  label: string;
  /** A hónapok sorrendjében (`months`). */
  values: string[];
  fill: string;
  swatch: string;
}

export function StackedTrendChart({
  months,
  series,
  fmt,
  fmtShort,
  monthLabel,
  ariaLabel,
}: {
  months: string[];
  series: TrendSeries[];
  fmt: Formatter;
  fmtShort: Formatter;
  monthLabel: (month: string) => string;
  ariaLabel: string;
}): JSX.Element {
  const rows = months.map((month, i) => {
    const row: Record<string, string | number> = { month, label: monthLabel(month) };
    for (const s of series) row[s.key] = toNum(s.values[i] ?? '0');
    return row;
  });
  // a halom TETEJE hónaponként: az utolsó NEM NULLA sorozat — az kapja a kerek véget (ha a legfelső sorozat abban
  // a hónapban 0, a látható teteje különben szögletes maradna)
  const topKeyOf = months.map((_, i) => {
    const top = [...series].reverse().find((s) => toNum(s.values[i] ?? '0') !== 0);
    return top?.key ?? null;
  });

  return (
    <div>
      <div className="mb-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs">
        {series.map((s) => (
          <LegendItem key={s.key} swatch={s.swatch} label={s.label} />
        ))}
      </div>
      <div className="text-muted-foreground h-64 w-full" aria-hidden="true">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={rows}
            margin={{ top: 8, right: 4, bottom: 0, left: 4 }}
            barCategoryGap="25%"
            accessibilityLayer
          >
            <CartesianGrid vertical={false} stroke={AXIS_LINE} strokeDasharray="3 3" />
            <XAxis
              dataKey="label"
              tick={TICK_STYLE}
              tickLine={false}
              axisLine={{ stroke: AXIS_LINE }}
              interval="preserveStartEnd"
              minTickGap={16}
            />
            <YAxis
              tick={<MoneyTick format={fmtShort} />}
              tickLine={false}
              axisLine={false}
              width={72}
            />
            <Tooltip
              cursor={{ className: 'fill-muted/40' }}
              content={({ active, label }) => {
                const i = rows.findIndex((r) => r.label === label);
                if (!active || i < 0) return null;
                return (
                  <TooltipCard
                    label={String(label)}
                    rows={series
                      .filter((s) => toNum(s.values[i] ?? '0') !== 0)
                      .map((s) => ({
                        key: s.key,
                        label: s.label,
                        value: fmt(s.values[i] ?? '0'),
                        swatch: s.swatch,
                      }))}
                  />
                );
              }}
            />
            {series.map((s) => (
              <Bar
                key={s.key}
                dataKey={s.key}
                stackId="a"
                className={s.fill}
                maxBarSize={28}
                // csak a halom (hónaponként) legfelső, nem nulla szelete kap lekerekítést
                shape={(props: BarShapeProps) => (
                  <Rectangle
                    {...props}
                    radius={topKeyOf[props.index] === s.key ? [3, 3, 0, 0] : 0}
                  />
                )}
              />
            ))}
          </BarChart>
        </ResponsiveContainer>
      </div>
      <SrOnlyData
        ariaLabel={ariaLabel}
        items={months.map((month, i) => ({
          key: month,
          label: monthLabel(month),
          value: series.map((s) => `${s.label} ${fmt(s.values[i] ?? '0')}`).join(', '),
        }))}
      />
    </div>
  );
}
